import React, { useState, useEffect, useRef } from 'react';
    import { Link } from 'react-router-dom';
    import { Input } from '@/components/ui/Input';
    import { Button } from '@/components/ui/Button';
    import { FiBriefcase, FiTruck, FiAlertCircle, FiMail, FiArrowLeft, FiSearch, FiChevronDown } from 'react-icons/fi';
    import { useAuth } from '@/contexts/AuthContext';
    import supabase from '@/supabase/supabase';
    import toast from 'react-hot-toast';

    const SL_DISTRICTS = [
      'Ampara', 'Anuradhapura', 'Badulla', 'Batticaloa', 'Colombo', 'Galle', 'Gampaha', 
      'Hambantota', 'Jaffna', 'Kalutara', 'Kandy', 'Kegalle', 'Kilinochchi', 'Kurunegala', 
      'Mannar', 'Matale', 'Matara', 'Monaragala', 'Mullaitivu', 'Nuwara Eliya', 'Polonnaruwa', 
      'Puttalam', 'Ratnapura', 'Trincomalee', 'Vavuniya'
    ];

    const COUNTRIES = [
      { name: 'Sri Lanka', code: '+94', iso: 'LK' },
      { name: 'United States', code: '+1', iso: 'US' },
      { name: 'United Kingdom', code: '+44', iso: 'GB' },
      { name: 'Australia', code: '+61', iso: 'AU' },
      { name: 'India', code: '+91', iso: 'IN' },
      { name: 'Malaysia', code: '+60', iso: 'MY' },
      { name: 'Singapore', code: '+65', iso: 'SG' },
      { name: 'United Arab Emirates', code: '+971', iso: 'AE' },
      { name: 'Other', code: '+', iso: '' }
    ];

    export function RegisterPage() {
      const [step, setStep] = useState(1);
      const [role, setRole] = useState(null);
      const [firstName, setFirstName] = useState('');
      const [lastName, setLastName] = useState('');
      const [email, setEmail] = useState('');
      const [password, setPassword] = useState('');
      const [phone, setPhone] = useState('');
      const [countryCode, setCountryCode] = useState('+94');
      const [district, setDistrict] = useState('');
      const [businessName, setBusinessName] = useState('');
      const [businessReg, setBusinessReg] = useState('');
      
      const [showCountryPicker, setShowCountryPicker] = useState(false);
      const [countrySearch, setCountrySearch] = useState('');
      const pickerRef = useRef(null);

      const [error, setError] = useState('');
      const [success, setSuccess] = useState(false);
      const [isLoading, setIsLoading] = useState(false);
      const [isResending, setIsResending] = useState(false);
      const [countdown, setCountdown] = useState(0);

      const { signUp } = useAuth();

      // IP Geolocation for Country Detection
      useEffect(() => {
        fetch('https://ipapi.co/json/')
          .then(res => res.json())
          .then(data => {
            const detected = COUNTRIES.find(c => c.iso === data.country);
            if (detected) setCountryCode(detected.code);
          })
          .catch(() => {});
      }, []);

      // Cooldown timer
      useEffect(() => {
        if (countdown > 0) {
          const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
          return () => clearTimeout(timer);
        }
      }, [countdown]);

      // Close dropdown on outside click
      useEffect(() => {
        const handleClick = (e) => {
          if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowCountryPicker(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
      }, []);

      const handlePhoneChange = (e) => {
        const value = e.target.value.replace(/\D/g, ''); // Digits only
        setPhone(value);
      };

      const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
          const { data: existingRole } = await supabase.rpc('check_user_exists', { p_email: email });
          if (existingRole) {
            setError(`An account already exists as a ${existingRole}.`);
            setIsLoading(false);
            return;
          }

          const metaData = {
            full_name: `${firstName} ${lastName}`.trim(),
            role,
            phone: `${countryCode}${phone}`,
            district: role === 'driver' ? district : undefined,
            business_name: role === 'business' ? businessName : undefined,
            business_registration_number: role === 'business' ? businessReg : undefined,
          };

          const { error: signUpError } = await signUp(email, password, metaData);
          if (signUpError) throw signUpError;
          
          setSuccess(true);
          setCountdown(60);
        } catch (err) {
          setError(err.message || 'Registration failed');
        } finally {
          setIsLoading(false);
        }
      };

      const handleResend = async () => {
        if (countdown > 0) return;
        setIsResending(true);
        try {
          const { error: resendErr } = await supabase.auth.resend({
            type: 'signup',
            email,
            options: { emailRedirectTo: `${window.location.origin}/login` }
          });
          if (resendErr) throw resendErr;
          toast.success('Verification link resent!');
          setCountdown(60);
        } catch (err) {
          toast.error(err.message || 'Failed to resend');
        } finally {
          setIsResending(false);
        }
      };

      const filteredCountries = COUNTRIES.filter(c => 
        c.name.toLowerCase().includes(countrySearch.toLowerCase()) || 
        c.code.includes(countrySearch)
      );

      if (success) {
        return (
          <div className="py-8 text-center space-y-6">
            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto">
              <FiMail className="w-10 h-10 text-emerald-500" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900 uppercase">Verify Your Email</h1>
              <p className="text-slate-500 mt-2">We sent a link to <b>{email}</b>. Please check your inbox and spam folder.</p>
            </div>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full h-12 rounded-xl font-bold">Return to Login</Button>
              </Link>
              <div className="pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-3">Didn't get it?</p>
                <Button 
                  variant="outline" 
                  onClick={handleResend} 
                  disabled={countdown > 0 || isResending}
                  className="w-full h-12 rounded-xl text-xs font-black uppercase tracking-widest"
                >
                  {isResending ? 'Sending...' : countdown > 0 ? `Resend in ${countdown}s` : 'Resend Verification Link'}
                </Button>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Create an account</h1>
            <p className="text-slate-500 text-sm mt-1">Select your profile type to begin</p>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3 text-red-600 text-sm">
              <FiAlertCircle className="shrink-0 mt-0.5" />
              <p className="font-bold">{error}</p>
            </div>
          )}

          {step === 1 ? (
            <div className="grid grid-cols-2 gap-4">
              <button onClick={() => { setRole('business'); setStep(2); }} className="p-6 rounded-2xl border-2 border-slate-200 hover:border-primary-400 text-left transition-all group">
                <FiBriefcase className="w-8 h-8 text-slate-400 mb-3 group-hover:text-primary-500" />
                <div className="font-black text-slate-900 uppercase text-xs">Business</div>
              </button>
              <button onClick={() => { setRole('driver'); setStep(2); }} className="p-6 rounded-2xl border-2 border-slate-200 hover:border-emerald-400 text-left transition-all group">
                <FiTruck className="w-8 h-8 text-slate-400 mb-3 group-hover:text-emerald-500" />
                <div className="font-black text-slate-900 uppercase text-xs">Driver</div>
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input label="First Name" value={firstName} onChange={e => setFirstName(e.target.value)} required />
                <Input label="Last Name" value={lastName} onChange={e => setLastName(e.target.value)} required />
              </div>

              <Input label="Email Address" type="email" value={email} onChange={e => setEmail(e.target.value)} required />

              <div className="space-y-1.5" ref={pickerRef}>
                <label className="text-sm font-medium text-slate-700">Phone Number</label>
                <div className="flex gap-2 relative">
                  <button type="button" onClick={() => setShowCountryPicker(!showCountryPicker)} className="h-10 w-24 rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold flex items-center justify-between">
                    {countryCode} <FiChevronDown />
                  </button>
                  <Input type="tel" value={phone} onChange={handlePhoneChange} placeholder="7X XXX XXXX" className="flex-1" required />
                  
                  {showCountryPicker && (
                    <div className="absolute top-12 left-0 w-64 bg-white border border-slate-200 shadow-2xl rounded-xl z-50 overflow-hidden">
                      <div className="p-2 border-b border-slate-100 flex items-center gap-2">
                        <FiSearch className="text-slate-400 ml-2" />
                        <input type="text" placeholder="Search country..." className="w-full h-8 text-sm outline-none" value={countrySearch} onChange={e => setCountrySearch(e.target.value)} autoFocus />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {filteredCountries.map(c => (
                          <button key={c.name} type="button" onClick={() => { setCountryCode(c.code); setShowCountryPicker(false); }} className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex justify-between">
                            <span>{c.name}</span> <span className="text-slate-400 font-bold">{c.code}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {role === 'business' && (
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Business Name" value={businessName} onChange={e => setBusinessName(e.target.value)} required />
                  <Input label="Reg Number" value={businessReg} onChange={e => setBusinessReg(e.target.value)} required />
                </div>
              )}

              {role === 'driver' && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">District</label>
                  <select value={district} onChange={e => setDistrict(e.target.value)} className="w-full h-10 border rounded-lg px-2 text-sm" required>
                    <option value="">Select...</option>
                    {SL_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              )}

              <Input label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} minLength={6} required />

              <div className="flex gap-3 pt-4">
                <Button variant="outline" type="button" onClick={() => setStep(1)} className="rounded-xl"><FiArrowLeft className="mr-2" /> Back</Button>
                <Button type="submit" isLoading={isLoading} className="flex-1 rounded-xl font-bold uppercase text-xs tracking-widest">Create Account</Button>
              </div>
            </form>
          )}
        </div>
      );
    }